/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactPortal } from 'react';
import { useReducer, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { jsx, cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { DocumentViewer } from '@atlaskit/media-document-viewer/document-viewer';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';

import { ZoomLevel } from './zoomLevel.ts';
import { i18n } from './viewPdfMacroI18n.ts';

export type FullscreenViewerProps = {
	getContent: (pageStart: number, pageEnd: number) => Promise<any>;
	getPageImageUrl: (pageNumber: number, zoom: number) => Promise<string>;
	onClose: () => void;
};

export type PresentationState = {
	zoomLevel: ZoomLevel;
	currentPage: number;
	totalPages: number;
};

export type PresentationAction =
	| { type: 'set_zoom_level'; zoomLevel: ZoomLevel }
	| { type: 'set_total_pages'; totalPages: number }
	| { type: 'go_to_page'; page: number };

const presentationInitialState: PresentationState = {
	zoomLevel: new ZoomLevel(1),
	currentPage: 1,
	totalPages: 0,
};

export const presentationReducer = (
	state: PresentationState,
	action: PresentationAction,
): PresentationState => {
	switch (action.type) {
		case 'set_zoom_level':
			return { ...state, zoomLevel: action.zoomLevel };
		case 'set_total_pages':
			return { ...state, totalPages: action.totalPages };
		case 'go_to_page': {
			const page = Math.max(1, Math.min(action.page, state.totalPages || 1));
			return { ...state, currentPage: page };
		}
		default:
			return state;
	}
};

export const scrollToCurrentPage = (
	container: HTMLDivElement | null,
	currentPage: number,
): void => {
	if (!container) {
		return;
	}

	const pageEl = container.querySelector(`#page-${currentPage}`);
	if (pageEl && typeof pageEl.scrollIntoView === 'function') {
		pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}
};

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = cssMap({
	// Full-viewport overlay backdrop
	backdrop: {
		position: 'fixed',
		inset: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- zIndex 510 is the ADS dialog layer value
		zIndex: 510,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: token('color.background.neutral.bold'),
	},
	// Inner content area (PDF + toolbar)
	overlay: {
		position: 'relative',
		width: '90vw',
		height: '85vh',
		display: 'flex',
		flexDirection: 'column',
	},
	// Scrollable PDF container
	viewerContainer: {
		position: 'relative',
		width: '100%',
		height: '100%',
		overflow: 'auto',
	},
	// Close button positioned above the overlay, top-right
	closeButton: {
		position: 'absolute',
		top: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- offset keeps close button above the overlay edge
		transform: 'translateY(-48px)',
		insetInlineEnd: 0,
	},
	toolbar: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.100'),
	},
	pageCounter: {
		minWidth: '64px',
		textAlign: 'center',
	},
	separator: {
		width: '1px',
		height: '20px',
		marginInline: token('space.050'),
		flexShrink: 0,
		backgroundColor: token('color.background.neutral'),
	},
	toolbarContainer: {
		display: 'flex',
		justifyContent: 'center',
		paddingBlock: token('space.150'),
	},
});

// ── PresentationToolbar ───────────────────────────────────────────────────────

type PresentationToolbarProps = {
	currentPage: number;
	totalPages: number;
	onPrevPage: () => void;
	onNextPage: () => void;
	onExit: () => void;
};

const PresentationToolbar = ({
	currentPage,
	totalPages,
	onPrevPage,
	onNextPage,
	onExit,
}: PresentationToolbarProps) => {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.toolbar} data-testid="viewpdf-presentation-toolbar">
			<Inline alignBlock="center" space="space.050">
				<IconButton
					icon={ArrowLeftIcon}
					label={formatMessage(i18n.previousPage)}
					isDisabled={currentPage <= 1}
					onClick={onPrevPage}
					appearance="subtle"
					spacing="compact"
					testId="viewpdf-prev-page"
				/>
				<div css={styles.pageCounter} data-testid="viewpdf-page-counter">
					<Text size="small" weight="medium" align="center">
						{totalPages > 0
							? formatMessage(i18n.pageCounter, { currentPage, totalPages })
							: String(currentPage)}
					</Text>
				</div>
				<IconButton
					icon={ArrowRightIcon}
					label={formatMessage(i18n.nextPage)}
					isDisabled={totalPages > 0 && currentPage >= totalPages}
					onClick={onNextPage}
					appearance="subtle"
					spacing="compact"
					testId="viewpdf-next-page"
				/>
				<div css={styles.separator} />
				<IconButton
					icon={CrossIcon}
					label={formatMessage(i18n.exitPresentationMode)}
					onClick={onExit}
					appearance="subtle"
					spacing="compact"
					testId="viewpdf-exit-presentation"
				/>
			</Inline>
		</div>
	);
};

// ── FullscreenViewer ──────────────────────────────────────────────────────────

export const FullscreenViewer = ({
	getContent,
	getPageImageUrl,
	onClose,
}: FullscreenViewerProps): ReactPortal => {
	const { formatMessage } = useIntl();
	const [state, dispatch] = useReducer(presentationReducer, presentationInitialState);
	const closeButtonRef = useRef<HTMLButtonElement | null>(null);
	const viewerContainerRef = useRef<HTMLDivElement | null>(null);
	const openerElementRef = useRef<Element | null>(null);

	// Capture the triggering element before the viewer steals focus
	useEffect(() => {
		openerElementRef.current = document.activeElement;
		return () => {
			const opener = openerElementRef.current;
			if (opener instanceof HTMLElement && opener.isConnected) {
				opener.focus();
			}
		};
	}, []);

	// Focus the close button on mount for accessibility
	useEffect(() => {
		closeButtonRef.current?.focus();
	}, []);

	// Fetch total page count on mount
	useEffect(() => {
		getContent(0, 1)
			.then((result: any) => {
				const total = result?.total_pages ?? 0;
				if (total > 0) {
					dispatch({ type: 'set_total_pages', totalPages: total });
				}
			})
			.catch(() => {
				// Silently ignore — page counter will show without total
			});
	}, [getContent]);

	// Scroll to the current page when it changes
	useEffect(() => {
		scrollToCurrentPage(viewerContainerRef.current, state.currentPage);
	}, [state.currentPage]);

	// Keyboard: Escape to close, Arrow/Page keys to navigate
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			} else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
				dispatch({ type: 'go_to_page', page: state.currentPage - 1 });
			} else if (e.key === 'ArrowRight' || e.key === 'PageDown') {
				dispatch({ type: 'go_to_page', page: state.currentPage + 1 });
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [onClose, state.currentPage]);

	const onPrevPage = useCallback(() => {
		dispatch({ type: 'go_to_page', page: state.currentPage - 1 });
	}, [state.currentPage]);

	const onNextPage = useCallback(() => {
		dispatch({ type: 'go_to_page', page: state.currentPage + 1 });
	}, [state.currentPage]);

	const handleBackdropClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			// Only close when clicking the backdrop itself, not the overlay content
			if (e.target === e.currentTarget) {
				onClose();
			}
		},
		[onClose],
	);

	const handleBackdropKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.target === e.currentTarget && (e.key === 'Escape' || e.key === 'Enter')) {
				onClose();
			}
		},
		[onClose],
	);

	const overlay = (
		<div
			css={styles.backdrop}
			data-testid="viewpdf-fullscreen-backdrop"
			onClick={handleBackdropClick}
			onKeyDown={handleBackdropKeyDown}
			role="presentation"
			tabIndex={-1}
		>
			<div
				css={styles.overlay}
				data-testid="viewpdf-fullscreen-overlay"
				role="dialog"
				aria-modal="true"
				aria-label={formatMessage(i18n.presentationMode)}
			>
				<div css={styles.closeButton}>
					<IconButton
						ref={closeButtonRef}
						icon={CrossIcon}
						label={formatMessage(i18n.closeFullscreen)}
						onClick={onClose}
						appearance="subtle"
						testId="viewpdf-fullscreen-close"
					/>
				</div>
				<div
					ref={viewerContainerRef}
					css={styles.viewerContainer}
					data-testid="viewpdf-fullscreen-viewer-container"
				>
					<DocumentViewer
						getContent={getContent}
						getPageImageUrl={getPageImageUrl}
						zoom={state.zoomLevel.value}
					/>
				</div>
				<div css={styles.toolbarContainer}>
					<Inline alignInline="center" space="space.0">
						<PresentationToolbar
							currentPage={state.currentPage}
							totalPages={state.totalPages}
							onPrevPage={onPrevPage}
							onNextPage={onNextPage}
							onExit={onClose}
						/>
					</Inline>
				</div>
			</div>
		</div>
	);

	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	return createPortal(overlay, document.body);
};
